import { useBeforeUnload, useBlocker, useNavigate } from '@remix-run/react'
import {
  ArchiveIcon,
  ArrowRightFromLineIcon,
  EllipsisVerticalIcon,
  TrashIcon,
} from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import { Alert, Badge, Button, Dropdown, IconButton, Portal } from '~/ui'
import { cn } from '~/utils/classnames'

import { useCollectionConfiguration } from './collection-configuration-context'
import {
  validateCollectionConfiguration,
  validateCollectionForPublishing,
  getCollectionFieldId,
} from './utils'
import { AlertDialog } from '../AlertDialog'

export const CollectionConfigurationActions = () => {
  const { t } = useTranslation()
  const shop = useShop()
  const navigate = useNavigate()
  const {
    collection,
    collectionItems,
    collectionAttachments,
    initialCollection,
    initialCollectionItems,
    initialCollectionAttachments,
    hasUnsavedChanges,
    liveValidationFields,
    setLiveValidationFields,
    liveErrors,
    setIsPublishingMode,
    dispatch,
    status,
    fetcher,
  } = useCollectionConfiguration()

  const navigationBlocker = useBlocker(hasUnsavedChanges)

  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (hasUnsavedChanges) {
          event.preventDefault()
        }
      },
      [hasUnsavedChanges]
    )
  )

  const hasFetcherError = useMemo(() => {
    if (
      !fetcher.data ||
      typeof fetcher.data !== 'object' ||
      !('errors' in fetcher.data)
    ) {
      return false
    }

    // TODO: add error logging
    return new Date().toISOString()
  }, [fetcher.data])

  const validateSubmission = useCallback(
    (shouldValidateForPublishing = false) => {
      // Set publishing mode for liveErrors computation
      setIsPublishingMode(shouldValidateForPublishing)

      const validationFunction = shouldValidateForPublishing
        ? validateCollectionForPublishing
        : validateCollectionConfiguration

      const allErrors = validationFunction({
        collection,
        status,
        collectionItems,
      })

      if (allErrors.size > 0) {
        setLiveValidationFields((prev) => {
          const newMap = new Map(prev)
          allErrors.forEach((_, key) => {
            newMap.set(key, new Date())
          })
          return newMap
        })

        // Check if name field has an error and scroll to it
        const nameFieldId = getCollectionFieldId(collection, 'name')
        if (allErrors.has(nameFieldId)) {
          const element = document.getElementById(nameFieldId)
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }

        return false
      }

      return true
    },
    [
      collection,
      setLiveValidationFields,
      status,
      collectionItems,
      setIsPublishingMode,
    ]
  )

  const handleSubmit = (
    data: SubmissionData = {},
    shouldValidateForPublishing = false
  ) => {
    const isValid = validateSubmission(shouldValidateForPublishing)
    if (!isValid) return

    // Create FormData with collection fields directly
    const formData = new FormData()
    const collectionData = { ...collection, ...data }

    Object.entries(collectionData).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        formData.append(key, String(value))
      }
    })

    // Add slug if not already present (for new collections)
    if (!collectionData.slug) {
      formData.append('slug', `collection-${new Date().getTime()}`)
    }

    collectionItems.forEach((item) => {
      formData.append('products[]', item.productId)
    })

    // Add collection attachments (for both new and existing collections)
    if (collectionAttachments.length > 0) {
      formData.append('attachments', JSON.stringify(collectionAttachments))
    }

    fetcher.submit(formData, {
      method: collectionData.id ? 'PATCH' : 'POST',
      action: makeShopOwnPath(shop, '/collections/api'),
    })
  }

  const handlePublish = () => {
    if (!collection.id) return

    const isValid = validateSubmission(true)
    if (!isValid) return

    // Just publish the collection (no changes to save)
    fetcher.submit(
      {
        _intent: 'publishCollection',
        id: collection.id,
      },
      {
        method: 'POST',
        action: makeShopOwnPath(shop, '/collections/api'),
      }
    )
  }

  const handleDiscardChanges = () => {
    setLiveValidationFields(new Map())
    dispatch({ type: 'patch', payload: initialCollection })
    // Also reset collection items and attachments
    dispatch({
      type: 'patch-collection-items',
      payload: initialCollectionItems,
    })
    dispatch({
      type: 'patch-collection-attachments',
      payload: initialCollectionAttachments,
    })
  }

  const handleArchive = () => {
    if (!collection.id) return

    fetcher.submit(
      {
        _intent: 'archiveCollection',
        id: collection.id,
      },
      {
        method: 'POST',
        action: makeShopOwnPath(shop, '/collections/api'),
      }
    )
  }

  const handleUnarchive = () => {
    if (!collection.id) return

    fetcher.submit(
      {
        _intent: 'restoreCollection',
        id: collection.id,
      },
      {
        method: 'POST',
        action: makeShopOwnPath(shop, '/collections/api'),
      }
    )
  }

  const handleUnpublish = () => {
    if (!collection.id) return

    fetcher.submit(
      {
        _intent: 'unpublishCollection',
        id: collection.id,
      },
      {
        method: 'POST',
        action: makeShopOwnPath(shop, '/collections/api'),
      }
    )
  }

  const handleDelete = () => {
    if (!collection.id) return

    navigate(makeShopOwnPath(shop, '/collections'), { replace: true })

    fetcher.submit(
      {
        _intent: 'deleteCollection',
        id: collection.id,
      },
      {
        method: 'POST',
        action: makeShopOwnPath(shop, '/collections/api'),
      }
    )
  }

  const liveValidationTimestamp = liveValidationFields
    .entries()
    .next()
    .value?.[1].toISOString()

  return (
    <div className="flex w-full flex-col items-end gap-1.5">
      {hasFetcherError && (
        <Alert
          type="error"
          title={t('page.products.form.generic_collection_submission_error')}
        />
      )}

      {liveErrors.has(getCollectionFieldId(collection, 'name')) && (
        <Alert
          type="error"
          title={t(
            `errors.${liveErrors.get(getCollectionFieldId(collection, 'name'))?.code}`
          )}
        />
      )}

      {liveErrors.has(getCollectionFieldId(collection, 'description')) && (
        <Alert
          type="error"
          title={t(
            `errors.${liveErrors.get(getCollectionFieldId(collection, 'description'))?.code}`
          )}
        />
      )}

      {liveErrors.has(getCollectionFieldId(collection, 'avatarSrc')) && (
        <Alert type="error" title={t('errors.collection_thumbnail_required')} />
      )}

      {liveErrors.has(getCollectionFieldId(collection, 'collectionItems')) && (
        <Alert
          type="error"
          title={t(
            `errors.${liveErrors.get(getCollectionFieldId(collection, 'collectionItems'))?.code}`
          )}
        />
      )}

      <div className="flex items-center gap-2">
        {hasUnsavedChanges && (
          <Button size="md" variant="ghost" onClick={handleDiscardChanges}>
            {t('action.discard_changes')}
          </Button>
        )}
        <SaveButton
          key={liveValidationTimestamp || hasFetcherError || 'submit'}
          isLoading={
            fetcher.state !== 'idle' && navigationBlocker.state !== 'blocked'
          }
          className={cn(
            (liveValidationFields.size > 0 || hasFetcherError) &&
              'animate-shake'
          )}
          onSubmit={handleSubmit}
          onPublish={handlePublish}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
          onUnpublish={handleUnpublish}
          onDelete={handleDelete}
        />
      </div>

      <AlertDialog
        isOpen={navigationBlocker.state === 'blocked'}
        title={t('page.products.form.confirmation_modal.title')}
        description={t('page.products.form.confirmation_modal.description')}
        cancelText={t('action.back_to_page')}
        confirmText={t('action.exit_without_saving')}
        onCancel={navigationBlocker.reset}
        onConfirm={navigationBlocker.proceed}
        isLoading={fetcher.state !== 'idle'}
      />
    </div>
  )
}

export const CollectionStatusBadge = ({
  className,
}: {
  className?: string
}) => {
  const { t } = useTranslation()
  const { status } = useCollectionConfiguration()

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Badge>
        <Badge.Status
          status={
            status === 'live'
              ? 'success'
              : status === 'archived' || status === 'unsaved'
                ? 'error'
                : 'neutral'
          }
        />
        {t(`noun.${status}`)}
      </Badge>
    </div>
  )
}

type SubmissionData = {
  publishedAt?: Date | null
  archivedAt?: Date | null
}

interface SaveButtonProps {
  className?: string
  isLoading?: boolean
  onSubmit: (
    data?: SubmissionData,
    shouldValidateForPublishing?: boolean
  ) => void
  onPublish: () => void
  onArchive: () => void
  onUnarchive: () => void
  onUnpublish: () => void
  onDelete: () => void
}

const SaveButton = ({
  className,
  isLoading,
  onSubmit,
  onPublish,
  onArchive,
  onUnarchive,
  onUnpublish,
  onDelete,
}: SaveButtonProps) => {
  const { t } = useTranslation()
  const { hasUnsavedChanges, status } = useCollectionConfiguration()

  const [openDialog, setOpenDialog] = useState<'unpublish' | 'delete' | null>(
    null
  )

  const [label, handleSubmit] = useMemo(() => {
    setOpenDialog(null)

    // if its publish, we can either publish changes or unpublish
    if (status === 'live') {
      if (hasUnsavedChanges) {
        // For published collections with changes, publish the changes
        // We need to validate for publishing since it's a published collection
        return [
          t('action.publish_changes'),
          () => {
            // Save changes with publishing validation
            onSubmit({}, true)
          },
        ]
      } else {
        return [t('action.unpublish'), () => setOpenDialog('unpublish')]
      }
    }

    // if its a draft, we can either save the changes to the current draft or publish it
    if (status === 'draft') {
      const label = hasUnsavedChanges
        ? t('action.save_draft')
        : t('action.publish')

      const handler = hasUnsavedChanges
        ? () => onSubmit({ publishedAt: null })
        : onPublish

      return [label, handler]
    }

    // if the collection is archived we can only unarchive it
    if (status === 'archived') {
      return [t('action.restore'), onUnarchive]
    }

    // if the collection is unsaved we can only create it
    if (status === 'unsaved') {
      return [
        t('action.create_collection'),
        () => onSubmit({ publishedAt: null }),
      ]
    }

    // else, just save whatever changes we have
    return [t('action.save_changes'), onSubmit]
  }, [status, t, onSubmit, hasUnsavedChanges, onUnarchive, onPublish])

  return (
    <>
      <Button.Group className={className}>
        <Button
          isLoading={!openDialog && isLoading}
          onClick={() => handleSubmit()}
        >
          {label}
        </Button>
        {status === 'draft' && !hasUnsavedChanges && (
          <Dropdown placement="top-end">
            <Dropdown.Trigger asChild>
              <IconButton aria-label={t('actions.more_options')}>
                <EllipsisVerticalIcon />
              </IconButton>
            </Dropdown.Trigger>
            <Portal>
              <Dropdown.Items className="min-w-72">
                <PublishDropdownItem variant="publish" onClick={onPublish} />
                <Dropdown.Divider />
                <PublishDropdownItem variant="archive" onClick={onArchive} />
                <PublishDropdownItem
                  variant="delete"
                  onClick={() => setOpenDialog('delete')}
                />
              </Dropdown.Items>
            </Portal>
          </Dropdown>
        )}
      </Button.Group>

      {/* unpublish */}
      <AlertDialog
        isOpen={openDialog === 'unpublish'}
        onCancel={() => setOpenDialog(null)}
        title={t('page.collections.publish_flow.unpublish.modal.title')}
        description={t(
          'page.collections.publish_flow.unpublish.modal.description'
        )}
        onConfirm={onUnpublish}
        isLoading={isLoading}
      />

      {/* delete */}
      <AlertDialog
        isOpen={openDialog === 'delete'}
        onCancel={() => setOpenDialog(null)}
        title={t('page.collections.publish_flow.delete.button.title')}
        description={t(
          'page.collections.publish_flow.delete.button.description'
        )}
        onConfirm={onDelete}
        isLoading={isLoading}
      />
    </>
  )
}

interface PublishDropdownItemProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Dropdown.Item>,
  'children' | 'type'
> {
  variant: 'publish' | 'archive' | 'delete'
}

const PUBLISH_VARIANTS = {
  publish: {
    title: 'page.collections.publish_flow.publish.button.title',
    description: 'page.collections.publish_flow.publish.button.description',
    icon: ArrowRightFromLineIcon,
  },
  archive: {
    title: 'page.collections.publish_flow.archive.button.title',
    description: 'page.collections.publish_flow.archive.button.description',
    icon: ArchiveIcon,
  },
  delete: {
    title: 'page.collections.publish_flow.delete.button.title',
    description: 'page.collections.publish_flow.delete.button.description',
    icon: TrashIcon,
  },
} as const

const PublishDropdownItem = ({
  variant,
  className,
  ...props
}: PublishDropdownItemProps) => {
  const { t } = useTranslation()

  const { icon: Icon, title, description } = PUBLISH_VARIANTS[variant]

  return (
    <Dropdown.Item
      className={cn('flex items-center gap-3 py-2', className)}
      {...props}
    >
      <div className="bg-foreground/6 flex size-8 shrink-0 items-center justify-center rounded-md">
        {Icon && <Icon className="size-4" />}
      </div>
      <div className="text-left text-sm">
        <div className="font-medium">{t(title)}</div>
        <div className="text-foreground/48">{t(description)}</div>
      </div>
    </Dropdown.Item>
  )
}
